import type { LookupItem } from '../../mocks/criminalMasterData';
import type { CivilAdviseSaveStep3 } from '../../types/civilAdviseApi';
import type { CriminalSaveStep1, CriminalSaveStep4, PhysicalFileDeliveryMethodRow } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { isOtherLookup } from '../wizard/criminal/criminalConstants';

export type ApiSubmitterStep = {
  submitted_agency_name?: string | null;
  submitted_division_name?: string | null;
  designation?: string | null;
  submitted_by_display_name?: string | null;
  /** Attorney General step 11 uses this field name from the API */
  submitted_by_user_display_name?: string | null;
  email?: string | null;
  email_address?: string | null;
  contact_no?: string | null;
  contact_number?: string | null;
};

export type SubmitterDisplay = {
  agency: string;
  division: string;
  designation: string;
  name: string;
  email: string;
  contact: string;
};

const EMPTY_API_VALUE = '—';

export function apiDisplayValue(
  value: string | null | undefined,
  emptyLabel: string = EMPTY_API_VALUE,
): string {
  return value?.trim() || emptyLabel;
}

export function submitterFromApiStep(step: ApiSubmitterStep | undefined): SubmitterDisplay {
  return {
    agency: apiDisplayValue(step?.submitted_agency_name),
    division: apiDisplayValue(step?.submitted_division_name),
    designation: apiDisplayValue(step?.designation),
    name: apiDisplayValue(
      step?.submitted_by_display_name ?? step?.submitted_by_user_display_name,
    ),
    email: apiDisplayValue(step?.email ?? step?.email_address),
    contact: apiDisplayValue(step?.contact_no ?? step?.contact_number),
  };
}

export function labelFor(id: string, items: LookupItem[]): string {
  return items.find((x) => x.id === id)?.name ?? (id ? '' : '—');
}

export function apiOrLookupLabel(apiName: string | undefined, id: string, items: LookupItem[]): string {
  const fromApi = apiName?.trim();
  if (fromApi) return fromApi;
  const fromLookup = labelFor(id, items);
  return fromLookup === '—' ? '' : fromLookup;
}

function formatOtherDeliveryLabel(label: string, otherText?: string): string {
  const other = otherText?.trim();
  if (other) return `${label} - ${other}`;
  return label;
}

function deliveryLabelsFromPhysicalMethods(
  raw?: string[] | PhysicalFileDeliveryMethodRow[],
): string[] | null {
  if (!raw?.length || typeof raw[0] === 'string') return null;

  const labels = (raw as PhysicalFileDeliveryMethodRow[])
    .map((row) => {
      const name = row.delivery_method_name?.trim() ?? '';
      if (!name) return '';
      const other = row.other_method?.trim();
      if (isOtherLookup(name)) return formatOtherDeliveryLabel(name, other);
      return name;
    })
    .filter(Boolean);

  return labels.length ? labels : null;
}

function deliveryLabelsFromStep4(step4?: CriminalSaveStep4): string[] | null {
  return deliveryLabelsFromPhysicalMethods(step4?.physical_file_delivery_methods);
}

export function deliveryMethodDisplay(
  draft: CriminalWizardDraft,
  deliveryMethods: LookupItem[],
  step4?: CriminalSaveStep4,
  noDataLabel = '—',
): string {
  const fromStep4 = deliveryLabelsFromStep4(step4);
  if (fromStep4?.length) return fromStep4.join(', ');

  if (draft.deliveryMethodIds.length) {
    const names = draft.deliveryMethodIds
      .map((id) => {
        const item = deliveryMethods.find((m) => m.id === id);
        if (item && isOtherLookup(item.name, item.id)) {
          return formatOtherDeliveryLabel(item.name, draft.deliveryOther);
        }
        return item?.name ?? id;
      })
      .filter(Boolean);
    if (names.length) return names.join(', ');
  }

  if (draft.deliveryMethods.length) {
    return draft.deliveryMethods
      .map((name) => {
        if (isOtherLookup(name)) return formatOtherDeliveryLabel(name, draft.deliveryOther);
        return name;
      })
      .join(', ');
  }

  return noDataLabel;
}

export function deliveryMethodDisplayFromStep3(
  draft: CriminalWizardDraft,
  deliveryMethods: LookupItem[],
  step3?: CivilAdviseSaveStep3,
  noDataLabel = '—',
): string {
  const fromStep3 = deliveryLabelsFromPhysicalMethods(step3?.physical_file_delivery_methods);
  if (fromStep3?.length) return fromStep3.join(', ');
  return deliveryMethodDisplay(draft, deliveryMethods, undefined, noDataLabel);
}

export function submitterFromStep3(step3: CivilAdviseSaveStep3 | undefined) {
  return submitterFromApiStep(step3);
}

export function caseSourceDisplay(
  draft: CriminalWizardDraft,
  caseSources: LookupItem[],
  step1?: CriminalSaveStep1,
): string {
  const item = caseSources.find((s) => s.id === draft.caseSourceId);
  if (item && isOtherLookup(item.name, item.code)) {
    return (
      draft.caseSourceOther ||
      step1?.case_source_description?.trim() ||
      step1?.caseSourceDescription?.trim() ||
      item.name
    );
  }
  return (
    step1?.case_source_name?.trim() ||
    labelFor(draft.caseSourceId, caseSources) ||
    draft.caseSource ||
    ''
  );
}

export function submitterFromStep4(step4: CriminalSaveStep4 | undefined) {
  return submitterFromApiStep(step4);
}

export function statusPillClass(statusName: string): string {
  const s = statusName.toLowerCase();
  if (s.includes('progress') || s.includes('review') || s.includes('registered')) return 'is-progress';
  if (s.includes('complete') || s.includes('approved')) return 'is-complete';
  if (s.includes('reject') || s.includes('return')) return 'is-reject';
  if (s.includes('draft')) return 'is-draft';
  return 'is-default';
}
